import { Fragment, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition } from '@headlessui/react';
import {
  ArrowLeftIcon,
  UserCircleIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  ClockIcon,
  DocumentTextIcon,
  CpuChipIcon,
  ShieldExclamationIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { complaintApi, rbacApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import FeatureGate from '../components/FeatureGate';
import { StatusBadge, LoadingSpinner } from '../components/ui';
import type { BadgeVariant } from '../components/ui';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_BADGE_MAP: Record<string, BadgeVariant> = {
  NEW: 'info',
  UNDER_INVESTIGATION: 'warning',
  PENDING_RESPONSE: 'warning',
  RESOLVED: 'success',
  ESCALATED: 'danger',
  CLOSED: 'neutral',
};

const PRIORITY_BADGE_MAP: Record<string, BadgeVariant> = {
  LOW: 'neutral',
  MEDIUM: 'info',
  HIGH: 'warning',
  CRITICAL: 'danger',
};

const STATUS_LABEL: Record<string, string> = {
  NEW: 'New',
  UNDER_INVESTIGATION: 'Under Investigation',
  PENDING_RESPONSE: 'Pending Response',
  RESOLVED: 'Resolved',
  ESCALATED: 'Escalated',
  CLOSED: 'Closed',
};

const CATEGORY_LABEL: Record<string, string> = {
  BILLING: 'Billing',
  SERVICE: 'Service',
  DISCRIMINATION: 'Discrimination',
  PRIVACY_COMPLAINT: 'Privacy',
  UNAUTHORIZED: 'Unauthorized',
  PRODUCT: 'Product',
  MARKETING_COMPLAINT: 'Marketing',
  OTHER_COMPLAINT: 'Other',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function maskEmail(email: string): string {
  if (!email) return '—';
  const [local, domain] = email.split('@');
  if (!domain) return email;
  const visible = local.slice(0, 2);
  return `${visible}${'*'.repeat(Math.max(local.length - 2, 2))}@${domain}`;
}

function maskAccountNumber(acct: string): string {
  if (!acct) return '—';
  if (acct.length <= 4) return '****';
  return '*'.repeat(acct.length - 4) + acct.slice(-4);
}

function formatDateTime(dateStr?: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Build a timeline from complaint status and date fields */
function buildTimeline(complaint: any) {
  const events: { label: string; date: string; icon: 'create' | 'update' | 'escalate' | 'resolve' | 'close' }[] = [];

  if (complaint.createdAt) {
    events.push({ label: 'Complaint Created', date: complaint.createdAt, icon: 'create' });
  }
  if (complaint.investigationStartedAt) {
    events.push({ label: 'Investigation Started', date: complaint.investigationStartedAt, icon: 'update' });
  }
  if (complaint.assignedAt) {
    events.push({ label: `Assigned to ${complaint.assignedTo || 'team member'}`, date: complaint.assignedAt, icon: 'update' });
  }
  if (complaint.escalatedAt) {
    events.push({ label: 'Escalated', date: complaint.escalatedAt, icon: 'escalate' });
  }
  if (complaint.respondedAt) {
    events.push({ label: 'Response Sent', date: complaint.respondedAt, icon: 'update' });
  }
  if (complaint.resolvedAt) {
    events.push({ label: 'Resolved', date: complaint.resolvedAt, icon: 'resolve' });
  }
  if (complaint.closedAt) {
    events.push({ label: 'Closed', date: complaint.closedAt, icon: 'close' });
  }

  events.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  return events;
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ComplaintDetailPage() {
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const { teamId } = useTeamId();

  const [showAssign, setShowAssign] = useState(false);
  const [showEscalate, setShowEscalate] = useState(false);
  const [showResolve, setShowResolve] = useState(false);

  // Assign form
  const [assignTo, setAssignTo] = useState('');

  // Resolve form
  const [rootCause, setRootCause] = useState('');
  const [resolution, setResolution] = useState('');

  // ── Query ───────────────────────────────────────────────────────────

  const { data: complaintData, isLoading } = useQuery({
    queryKey: ['complaint', teamId, id],
    queryFn: () => complaintApi.get(teamId!, id!),
    enabled: !!teamId && !!id,
  });
  const complaint = complaintData?.data;

  // Fetch the per-complaint analysis. Polls every 5s while it's missing,
  // since the analyzer runs async after creation. Stops once data lands
  // (or a 404 — the analyzer never ran).
  const { data: analysisData } = useQuery({
    queryKey: ['complaint-analysis', teamId, id],
    queryFn: () => complaintApi.getAnalysis(teamId!, id!).then(r => r.data),
    enabled: !!teamId && !!id,
    retry: false,
    refetchInterval: (q) => (q.state.data ? false : 5000),
  });
  const analysis: any = analysisData ?? null;

  const reanalyzeMutation = useMutation({
    mutationFn: () => complaintApi.reanalyze(teamId!, id!).then(r => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(['complaint-analysis', teamId, id], data);
      queryClient.invalidateQueries({ queryKey: ['complaint', teamId, id] });
      toast.success('Analysis refreshed');
    },
    onError: (err: any) =>
      toast.error(err.response?.data?.error || 'Re-analysis failed'),
  });

  const { data: membersData } = useQuery({
    queryKey: ['team-members', teamId],
    queryFn: () => rbacApi.getTeamMembers(teamId!),
    enabled: !!teamId && showAssign,
  });
  const teamMembers: { id: string; name: string; email: string; role: string }[] =
    membersData?.data?.members || membersData?.data || [];

  // ── Mutations ───────────────────────────────────────────────────────

  const assignMutation = useMutation({
    mutationFn: (data: { assignedTo: string }) =>
      complaintApi.assign(teamId!, id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['complaint', teamId, id] });
      queryClient.invalidateQueries({ queryKey: ['complaints'] });
      queryClient.invalidateQueries({ queryKey: ['complaintDashboard'] });
      toast.success('Complaint assigned successfully');
      setShowAssign(false);
      setAssignTo('');
    },
    onError: (err: any) =>
      toast.error(err.response?.data?.error || 'Failed to assign complaint'),
  });

  const escalateMutation = useMutation({
    mutationFn: () => complaintApi.escalate(teamId!, id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['complaint', teamId, id] });
      queryClient.invalidateQueries({ queryKey: ['complaints'] });
      queryClient.invalidateQueries({ queryKey: ['complaintDashboard'] });
      toast.success('Complaint escalated');
      setShowEscalate(false);
    },
    onError: (err: any) =>
      toast.error(err.response?.data?.error || 'Failed to escalate complaint'),
  });

  const resolveMutation = useMutation({
    mutationFn: (data: { rootCause: string; resolution: string }) =>
      complaintApi.resolve(teamId!, id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['complaint', teamId, id] });
      queryClient.invalidateQueries({ queryKey: ['complaints'] });
      queryClient.invalidateQueries({ queryKey: ['complaintDashboard'] });
      toast.success('Complaint resolved');
      setShowResolve(false);
      setRootCause('');
      setResolution('');
    },
    onError: (err: any) =>
      toast.error(err.response?.data?.error || 'Failed to resolve complaint'),
  });

  // ── Loading / Error ─────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!complaint) {
    return (
      <div className="py-16 text-center">
        <p className="text-gray-500">Complaint not found.</p>
        <Link
          to="/complaints"
          className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-teal-600 hover:text-teal-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Complaints
        </Link>
      </div>
    );
  }

  const timeline = buildTimeline(complaint);
  const isResolved = complaint.status === 'RESOLVED' || complaint.status === 'CLOSED';

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <FeatureGate feature="complaint_management">
      <div className="space-y-6">
        {/* Back link */}
        <Link
          to="/complaints"
          className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Complaints
        </Link>

        {/* Header row */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold text-navy-900">
              {complaint.referenceNumber}
            </h1>
            <StatusBadge variant={STATUS_BADGE_MAP[complaint.status] || 'neutral'}>
              {STATUS_LABEL[complaint.status] || complaint.status}
            </StatusBadge>
            <StatusBadge variant={PRIORITY_BADGE_MAP[complaint.priority] || 'neutral'}>
              {complaint.priority}
            </StatusBadge>
            <StatusBadge variant="info">
              {CATEGORY_LABEL[complaint.category] || complaint.category}
            </StatusBadge>
          </div>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* ── Left Column (col-span-2) ─────────────────────────────── */}
          <div className="space-y-6 lg:col-span-2">
            {/* Subject + Description */}
            <div className="rounded-lg border border-gray-100 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-navy-900">
                {complaint.subject}
              </h2>
              <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                {complaint.description}
              </p>
            </div>

            {/* Intelligence panel — Phase 1+4 of complaint intelligence
                (Varo, May 2026). Shows per-complaint analyzer output:
                regulatory flags, AI risk score, classification mismatch,
                themes, and the human-readable needs-review reasons. */}
            {analysis && (
              <div className={clsx(
                'rounded-lg border p-6 shadow-sm',
                analysis.needsReview ? 'border-amber-300 bg-amber-50/30' : 'border-gray-100 bg-white',
              )}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-navy-900 flex items-center gap-2">
                      Intelligence
                      {analysis.needsReview && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-900 text-[10px] font-semibold px-2 py-0.5 uppercase tracking-wide">
                          Needs review
                        </span>
                      )}
                    </h3>
                    <p className="mt-0.5 text-xs text-gray-500">
                      Last analyzed {new Date(analysis.analyzedAt).toLocaleString()}
                      {analysis.aiModelUsed ? ` · ${analysis.aiModelUsed}` : ' · deterministic only'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => reanalyzeMutation.mutate()}
                    disabled={reanalyzeMutation.isPending}
                    className="text-xs font-semibold text-teal-700 hover:text-teal-900 disabled:opacity-50"
                  >
                    {reanalyzeMutation.isPending ? 'Re-analyzing…' : 'Re-analyze'}
                  </button>
                </div>

                {/* Top stat row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  <div className="rounded-md bg-white border border-gray-200 p-2">
                    <div className="text-[10px] uppercase tracking-wide text-gray-500">Risk score</div>
                    <div className={clsx(
                      'text-xl font-bold tabular-nums',
                      analysis.combinedRiskScore >= 80 ? 'text-red-700' :
                      analysis.combinedRiskScore >= 60 ? 'text-orange-700' :
                      analysis.combinedRiskScore >= 40 ? 'text-amber-700' : 'text-gray-900',
                    )}>
                      {analysis.combinedRiskScore}
                    </div>
                  </div>
                  <div className="rounded-md bg-white border border-gray-200 p-2">
                    <div className="text-[10px] uppercase tracking-wide text-gray-500">AI risk</div>
                    <div className="text-xl font-bold tabular-nums text-gray-900">
                      {analysis.aiRiskScore ?? '—'}
                    </div>
                  </div>
                  <div className="rounded-md bg-white border border-gray-200 p-2">
                    <div className="text-[10px] uppercase tracking-wide text-gray-500">AI priority</div>
                    <div className="text-xl font-bold text-gray-900">
                      {analysis.aiPrioritySuggested ?? '—'}
                    </div>
                  </div>
                  <div className="rounded-md bg-white border border-gray-200 p-2">
                    <div className="text-[10px] uppercase tracking-wide text-gray-500">AI confidence</div>
                    <div className="text-xl font-bold tabular-nums text-gray-900">
                      {analysis.aiClassificationConfidence != null
                        ? `${(analysis.aiClassificationConfidence * 100).toFixed(0)}%`
                        : '—'}
                    </div>
                  </div>
                </div>

                {/* Classification mismatch callout */}
                {analysis.classificationMismatch && (analysis.aiCategoriesProposed?.length ?? 0) > 0 && (
                  <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-3 text-xs">
                    <div className="font-semibold text-blue-900 mb-1">
                      Filed category disagrees with AI's primary suggestion
                    </div>
                    <div className="text-blue-800">
                      Filed as <span className="font-mono uppercase">{complaint.category}</span> ·
                      AI primary: <span className="font-mono uppercase">{analysis.aiCategoriesProposed[0]}</span>
                    </div>
                  </div>
                )}

                {/* AI summary */}
                {analysis.aiSummary && (
                  <div className="mb-4">
                    <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">AI summary</div>
                    <p className="text-sm text-gray-800">{analysis.aiSummary}</p>
                  </div>
                )}

                {/* Reasons */}
                {(analysis.needsReviewReasons?.length ?? 0) > 0 && (
                  <div className="mb-4">
                    <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Why this is flagged</div>
                    <ul className="text-xs text-gray-800 list-disc list-inside space-y-0.5">
                      {analysis.needsReviewReasons.map((r: string) => <li key={r}>{r}</li>)}
                    </ul>
                  </div>
                )}

                {/* Regulatory flags + themes */}
                <div className="flex flex-wrap gap-2 mb-2">
                  {(analysis.regulatoryFlags ?? []).map((flag: string) => (
                    <span key={flag} className="inline-flex bg-red-50 border border-red-200 text-red-800 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full">
                      {flag}
                    </span>
                  ))}
                  {(analysis.aiThemes ?? []).map((t: string) => (
                    <span key={t} className="inline-flex bg-teal-50 border border-teal-200 text-teal-800 text-[10px] font-medium px-2 py-0.5 rounded-full">
                      {t}
                    </span>
                  ))}
                </div>

                {/* Red-flag keywords */}
                {(analysis.redFlagKeywords?.length ?? 0) > 0 && (
                  <div className="mt-2 text-[11px] text-gray-600">
                    <span className="text-gray-500 mr-1">Matched phrases:</span>
                    {analysis.redFlagKeywords.slice(0, 8).map((k: string, i: number) => (
                      <span key={k + i}>
                        <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">{k}</span>
                        {i < Math.min(7, analysis.redFlagKeywords.length - 1) ? ' ' : ''}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Customer Information */}
            <div className="rounded-lg border border-gray-100 bg-white p-6 shadow-sm">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-navy-900">
                <UserCircleIcon className="h-5 w-5 text-gray-400" />
                Customer Information
              </h3>
              <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
                    Name
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {complaint.customerName || '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
                    Email
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {complaint.customerEmail
                      ? maskEmail(complaint.customerEmail)
                      : '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
                    Phone
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {complaint.customerPhone || '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
                    Account Number
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {complaint.customerAccountNumber
                      ? maskAccountNumber(complaint.customerAccountNumber)
                      : '—'}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Desired Resolution */}
            {complaint.desiredResolution && (
              <div className="rounded-lg border border-gray-100 bg-white p-6 shadow-sm">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-navy-900">
                  <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                  Desired Resolution
                </h3>
                <p className="mt-3 whitespace-pre-wrap text-sm text-gray-700">
                  {complaint.desiredResolution}
                </p>
              </div>
            )}

            {/* Timeline */}
            {timeline.length > 0 && (
              <div className="rounded-lg border border-gray-100 bg-white p-6 shadow-sm">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-navy-900">
                  <ClockIcon className="h-5 w-5 text-gray-400" />
                  Timeline
                </h3>
                <ol className="mt-4 space-y-0">
                  {timeline.map((event, idx) => (
                    <li key={idx} className="relative flex gap-4 pb-6 last:pb-0">
                      {/* Vertical line */}
                      {idx < timeline.length - 1 && (
                        <span className="absolute left-[11px] top-6 h-full w-0.5 bg-gray-200" />
                      )}
                      {/* Dot */}
                      <span
                        className={clsx(
                          'relative z-10 mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                          event.icon === 'create' && 'bg-blue-100',
                          event.icon === 'update' && 'bg-teal-100',
                          event.icon === 'escalate' && 'bg-red-100',
                          event.icon === 'resolve' && 'bg-green-100',
                          event.icon === 'close' && 'bg-gray-100',
                        )}
                      >
                        <span
                          className={clsx(
                            'h-2 w-2 rounded-full',
                            event.icon === 'create' && 'bg-blue-500',
                            event.icon === 'update' && 'bg-teal-500',
                            event.icon === 'escalate' && 'bg-red-500',
                            event.icon === 'resolve' && 'bg-green-500',
                            event.icon === 'close' && 'bg-gray-500',
                          )}
                        />
                      </span>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {event.label}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDateTime(event.date)}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>

          {/* ── Right Column (col-span-1) ─────────────────────────────── */}
          <div className="space-y-6">
            {/* Actions Panel */}
            <div className="rounded-lg border border-gray-100 bg-white p-6 shadow-sm">
              <h3 className="text-sm font-semibold text-navy-900">Actions</h3>
              <div className="mt-4 flex flex-col gap-3">
                <button
                  onClick={() => setShowAssign(true)}
                  className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <UserCircleIcon className="h-4 w-4" />
                  Assign
                </button>
                <button
                  onClick={() => setShowEscalate(true)}
                  disabled={complaint.status === 'ESCALATED' || isResolved}
                  className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ExclamationTriangleIcon className="h-4 w-4" />
                  Escalate
                </button>
                <button
                  onClick={() => setShowResolve(true)}
                  disabled={isResolved}
                  className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <CheckCircleIcon className="h-4 w-4" />
                  Resolve
                </button>
              </div>
            </div>

            {/* AI Categorization */}
            <div className="rounded-lg border border-gray-100 bg-white p-6 shadow-sm">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-navy-900">
                <CpuChipIcon className="h-5 w-5 text-gray-400" />
                AI Categorization
              </h3>
              <div className="mt-3">
                {complaint.aiCategorization ? (
                  <div className="space-y-2">
                    {typeof complaint.aiCategorization === 'object' ? (
                      Object.entries(complaint.aiCategorization).map(
                        ([key, value]) => (
                          <div key={key} className="flex items-start justify-between gap-2">
                            <span className="text-xs font-medium text-gray-500 capitalize">
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </span>
                            <span className="text-xs text-gray-900 text-right max-w-[60%] break-words">
                              {typeof value === 'number'
                                ? `${(value * 100).toFixed(1)}%`
                                : String(value)}
                            </span>
                          </div>
                        ),
                      )
                    ) : (
                      <p className="text-sm text-gray-700">
                        {String(complaint.aiCategorization)}
                      </p>
                    )}
                  </div>
                ) : (
                  <StatusBadge variant="warning">Pending</StatusBadge>
                )}
              </div>
            </div>

            {/* Regulatory Body */}
            {complaint.regulatoryBody && (
              <div className="rounded-lg border border-gray-100 bg-white p-6 shadow-sm">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-navy-900">
                  <ShieldExclamationIcon className="h-5 w-5 text-gray-400" />
                  Regulatory
                </h3>
                <p className="mt-2 text-sm font-medium text-gray-900">
                  {complaint.regulatoryBody}
                </p>
              </div>
            )}

            {/* Related Report */}
            {complaint.relatedReportId && (
              <div className="rounded-lg border border-gray-100 bg-white p-6 shadow-sm">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-navy-900">
                  <LinkIcon className="h-5 w-5 text-gray-400" />
                  Related Report
                </h3>
                <Link
                  to={`/reports/${complaint.relatedReportId}`}
                  className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-teal-600 hover:text-teal-700 hover:underline"
                >
                  View Report
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* ── Resolution Section (below grid) ────────────────────────── */}
        {isResolved && (complaint.rootCause || complaint.resolution) && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-6">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-green-900">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
              Resolution
            </h3>
            <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
              {complaint.rootCause && (
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wider text-green-700">
                    Root Cause
                  </dt>
                  <dd className="mt-1 whitespace-pre-wrap text-sm text-green-900">
                    {complaint.rootCause}
                  </dd>
                </div>
              )}
              {complaint.resolution && (
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wider text-green-700">
                    Resolution
                  </dt>
                  <dd className="mt-1 whitespace-pre-wrap text-sm text-green-900">
                    {complaint.resolution}
                  </dd>
                </div>
              )}
            </div>
            <div className="mt-4 flex flex-wrap gap-6 border-t border-green-200 pt-4">
              {complaint.resolvedBy && (
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wider text-green-700">
                    Resolved By
                  </dt>
                  <dd className="mt-1 text-sm text-green-900">
                    {complaint.resolvedBy}
                  </dd>
                </div>
              )}
              {complaint.resolvedAt && (
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wider text-green-700">
                    Resolved At
                  </dt>
                  <dd className="mt-1 text-sm text-green-900">
                    {formatDateTime(complaint.resolvedAt)}
                  </dd>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Assign Dialog ──────────────────────────────────────────── */}
        <Transition appear show={showAssign} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50"
            onClose={() => setShowAssign(false)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>
            <div className="fixed inset-0 flex items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-lg font-semibold text-navy-900">
                      Assign Complaint
                    </Dialog.Title>
                    <button
                      onClick={() => setShowAssign(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Assign To
                    </label>
                    <select
                      value={assignTo}
                      onChange={(e) => setAssignTo(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                    >
                      <option value="">Select a team member</option>
                      {teamMembers.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name || m.email} — {m.role}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mt-5 flex justify-end gap-3">
                    <button
                      onClick={() => setShowAssign(false)}
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() =>
                        assignTo.trim() &&
                        assignMutation.mutate({ assignedTo: assignTo.trim() })
                      }
                      disabled={!assignTo.trim() || assignMutation.isPending}
                      className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50 transition-colors"
                    >
                      {assignMutation.isPending ? 'Assigning...' : 'Assign'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition>

        {/* ── Escalate Confirm Dialog ─────────────────────────────────── */}
        <Transition appear show={showEscalate} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50"
            onClose={() => setShowEscalate(false)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>
            <div className="fixed inset-0 flex items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-lg font-semibold text-navy-900">
                      Escalate Complaint
                    </Dialog.Title>
                    <button
                      onClick={() => setShowEscalate(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                  <p className="mt-3 text-sm text-gray-600">
                    Are you sure you want to escalate this complaint? This will
                    flag it for immediate attention and notify relevant parties.
                  </p>
                  <div className="mt-5 flex justify-end gap-3">
                    <button
                      onClick={() => setShowEscalate(false)}
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => escalateMutation.mutate()}
                      disabled={escalateMutation.isPending}
                      className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      {escalateMutation.isPending
                        ? 'Escalating...'
                        : 'Confirm Escalate'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition>

        {/* ── Resolve Dialog ──────────────────────────────────────────── */}
        <Transition appear show={showResolve} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50"
            onClose={() => setShowResolve(false)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>
            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
                    <div className="flex items-center justify-between">
                      <Dialog.Title className="text-lg font-semibold text-navy-900">
                        Resolve Complaint
                      </Dialog.Title>
                      <button
                        onClick={() => setShowResolve(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Root Cause <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={rootCause}
                          onChange={(e) => setRootCause(e.target.value)}
                          rows={3}
                          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                          placeholder="What caused this complaint?"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Resolution <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={resolution}
                          onChange={(e) => setResolution(e.target.value)}
                          rows={3}
                          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                          placeholder="How was the complaint resolved?"
                        />
                      </div>
                    </div>
                    <div className="mt-5 flex justify-end gap-3">
                      <button
                        onClick={() => setShowResolve(false)}
                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          if (!rootCause.trim()) {
                            toast.error('Root cause is required');
                            return;
                          }
                          if (!resolution.trim()) {
                            toast.error('Resolution is required');
                            return;
                          }
                          resolveMutation.mutate({
                            rootCause: rootCause.trim(),
                            resolution: resolution.trim(),
                          });
                        }}
                        disabled={resolveMutation.isPending}
                        className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50 transition-colors"
                      >
                        {resolveMutation.isPending
                          ? 'Resolving...'
                          : 'Resolve Complaint'}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </FeatureGate>
  );
}
